import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { User, Stethoscope, Loader2 } from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

export default function SelectRole() {
  const { user, token, login } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(null);

  const handleSelect = async (userType) => {
    setSubmitting(userType);
    try {
      const res = await fetch(`${API}/auth/select-role`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ user_type: userType })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Une erreur est survenue");
      }
      const data = await res.json();
      login(data.user, token);
      toast.success("Compte configuré !");
      if (userType === "nurse") {
        navigate("/nurse/onboarding", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      toast.error(e.message);
      setSubmitting(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center px-4 py-12" data-testid="select-role-screen">
      <div className="w-full max-w-2xl text-center">
        <img src={LOGO_URL} alt="Infirmiers Privés" className="h-14 w-auto mx-auto mb-8" />

        <h1 className="text-2xl sm:text-3xl font-semibold text-white">
          Bienvenue{user?.name ? `, ${user.name}` : ""} !
        </h1>
        <p className="mt-3 text-base text-[#94A3B8]">
          Pour finaliser votre inscription, indiquez-nous qui vous êtes.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <button
            type="button"
            onClick={() => handleSelect("patient")}
            disabled={!!submitting}
            className="group bg-[#1F2A38] border border-[#2A3A4D] rounded-2xl p-8 text-left transition-colors hover:border-[#00E073] disabled:opacity-60"
            data-testid="select-role-patient-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-[#00E073]/15 flex items-center justify-center mb-5">
              {submitting === "patient" ? (
                <Loader2 className="w-6 h-6 text-[#00E073] animate-spin" />
              ) : (
                <User className="w-6 h-6 text-[#00E073]" />
              )}
            </div>
            <h2 className="text-lg font-semibold text-white">Je suis un patient</h2>
            <p className="mt-2 text-sm text-[#94A3B8]">
              Je recherche un infirmier pour des soins à domicile.
            </p>
          </button>

          <button
            type="button"
            onClick={() => handleSelect("nurse")}
            disabled={!!submitting}
            className="group bg-[#1F2A38] border border-[#2A3A4D] rounded-2xl p-8 text-left transition-colors hover:border-[#28CAD4] disabled:opacity-60"
            data-testid="select-role-nurse-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-[#28CAD4]/15 flex items-center justify-center mb-5">
              {submitting === "nurse" ? (
                <Loader2 className="w-6 h-6 text-[#28CAD4] animate-spin" />
              ) : (
                <Stethoscope className="w-6 h-6 text-[#28CAD4]" />
              )}
            </div>
            <h2 className="text-lg font-semibold text-white">Je suis un infirmier</h2>
            <p className="mt-2 text-sm text-[#94A3B8]">
              Je suis infirmier libéral et je souhaite proposer mes services.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
